import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Table, Badge, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
// Remove unused imports as we now use database as single source of truth
import { getLenderSettings, saveLenderSettings, createEmailOnlyLender, deleteLender } from '../api/lenderSettingsApi';
import '../styles/FieldMappings.css'; // Reuse the same styles as FieldMappings

const splitEmailList = (value) => {
  return String(value || '')
    .split(/[;,]/)
    .map(email => email.trim())
    .filter(Boolean);
};

const normalizeEmailList = (value) => splitEmailList(value).join(', ');

const getInvalidEmails = (value) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return splitEmailList(value).filter(email => !emailRegex.test(email));
};

/**
 * CustomEmailLenderForm component for creating new email-only lenders
 */
const CustomEmailLenderForm = ({ onLenderCreated }) => {
  const [formData, setFormData] = useState({
    stringId: '',
    lenderName: '',
    emailToAddress: '',
    emailCcAddress: '',
    emailSubject: '',
    emailBody: '',
    tier: 1,
    financingType: 'working-capital',
    crmAccountId: ''
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);
  const { success, error: notifyError } = useNotifications();

  // Auto-generate stringId from lenderName
  const generateStringId = (lenderName) => {
    return lenderName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '-')        // Replace spaces with hyphens
      .trim();
  };

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // Auto-generate stringId when lenderName changes
    if (field === 'lenderName') {
      updates.stringId = generateStringId(value);
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    setFormError(null); // Clear error when user starts typing
  };

  const validateForm = () => {
    if (!formData.lenderName.trim()) return 'Lender Name is required';
    if (!formData.emailToAddress.trim()) return 'Email To Address is required';
    
    const invalidToEmails = getInvalidEmails(formData.emailToAddress);
    const invalidCcEmails = getInvalidEmails(formData.emailCcAddress);
    if (invalidToEmails.length > 0) return `Invalid Email To Address: ${invalidToEmails.join(', ')}`;
    if (invalidCcEmails.length > 0) return `Invalid Email CC Address: ${invalidCcEmails.join(', ')}`;
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setCreating(true);
      setFormError(null);

      await createEmailOnlyLender({
        ...formData,
        emailToAddress: normalizeEmailList(formData.emailToAddress),
        emailCcAddress: normalizeEmailList(formData.emailCcAddress)
      });
      
      success('Email-only lender created successfully');
      
      // Reset form
      setFormData({
        stringId: '',
        lenderName: '',
        emailToAddress: '',
        emailCcAddress: '',
        emailSubject: '',
        emailBody: '',
        tier: 1,
        financingType: 'working-capital',
        crmAccountId: ''
      });
      
      // Refresh the parent component's data
      if (onLenderCreated) {
        onLenderCreated();
      }
      
    } catch (error) {
      console.error('Error creating email-only lender:', error);
      const errorMessage = error.response?.data?.message || 'Failed to create email-only lender';
      setFormError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {formError && (
        <Alert variant="danger" className="mb-3">
          {formError}
        </Alert>
      )}
      
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Lender Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Custom Lender Corp"
              value={formData.lenderName}
              onChange={(e) => handleInputChange('lenderName', e.target.value)}
              disabled={creating}
              required
            />
            <Form.Text className="text-muted">
              Enter the full name of the lender. A unique ID will be generated automatically.
            </Form.Text>
            {formData.lenderName && formData.stringId && (
              <Form.Text className="text-info">
                <strong>Generated Lender ID:</strong> {formData.stringId}
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Email To Address *</Form.Label>
            <Form.Control
              type="text"
              placeholder="submissions@lender.com, manager@lender.com"
              value={formData.emailToAddress}
              onChange={(e) => handleInputChange('emailToAddress', e.target.value)}
              disabled={creating}
              required
            />
            <Form.Text className="text-muted">
              Separate multiple addresses with commas or semicolons.
            </Form.Text>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Email CC Address</Form.Label>
            <Form.Control
              type="text"
              placeholder="manager@lender.com; team@lender.com (optional)"
              value={formData.emailCcAddress}
              onChange={(e) => handleInputChange('emailCcAddress', e.target.value)}
              disabled={creating}
            />
            <Form.Text className="text-muted">
              Separate multiple addresses with commas or semicolons.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Custom Email Subject</Form.Label>
            <Form.Control
              type="text"
              placeholder="Leave blank to use default: [Client Name] New Submission - [Business Name]"
              value={formData.emailSubject}
              onChange={(e) => handleInputChange('emailSubject', e.target.value)}
              disabled={creating}
              maxLength={200}
            />
            <Form.Text className="text-muted">
              Optional custom subject line for email submissions. Use [BusinessName] and [ClientName] as placeholders.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Custom Email Body</Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Leave blank for default simple message with attachments only"
              value={formData.emailBody}
              onChange={(e) => handleInputChange('emailBody', e.target.value)}
              disabled={creating}
              maxLength={2000}
            />
            <Form.Text className="text-muted">
              Optional custom message body for email submissions. Use [BusinessName] and [ClientName] as placeholders.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Tier</Form.Label>
            <Form.Select
              value={formData.tier}
              onChange={(e) => handleInputChange('tier', parseInt(e.target.value))}
              disabled={creating}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Financing Type</Form.Label>
            <Form.Select
              value={formData.financingType}
              onChange={(e) => handleInputChange('financingType', e.target.value)}
              disabled={creating}
            >
              <option value="working-capital">Working Capital</option>
              <option value="equipment-financing">Equipment Financing</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>CRM Account ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Optional CRM ID"
              value={formData.crmAccountId}
              onChange={(e) => handleInputChange('crmAccountId', e.target.value)}
              disabled={creating}
              maxLength={100}
            />
          </Form.Group>
        </Col>
      </Row>
      
      <div className="d-flex justify-content-end">
        <Button
          type="submit"
          variant="success"
          disabled={creating}
        >
          {creating ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : 'Create Email Lender'}
        </Button>
      </div>
    </Form>
  );
};

/**
 * ApiLenderForm component for creating new API lenders (developers only)
 */
const ApiLenderForm = ({ onLenderCreated }) => {
  const [formData, setFormData] = useState({
    stringId: '',
    lenderName: '',
    financingType: 'working-capital',
    tier: 1,
    crmAccountId: ''
  });
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState(null);
  const { success, error: notifyError } = useNotifications();

  // Auto-generate stringId from lenderName
  const generateStringId = (lenderName) => {
    return lenderName
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special characters
      .replace(/\s+/g, '-')        // Replace spaces with hyphens
      .trim();
  };

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // Auto-generate stringId when lenderName changes
    if (field === 'lenderName') {
      updates.stringId = generateStringId(value);
    }
    
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
    setFormError(null);
  };

  const validateForm = () => {
    if (!formData.lenderName.trim()) return 'Lender Name is required';
    if (!formData.financingType) return 'Financing Type is required';
    
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setCreating(true);
      setFormError(null);

      // Call API to create API lender
      const response = await fetch('/api/lender-settings/api-lender', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create API lender');
      }

      success('API lender created successfully');
      
      // Reset form
      setFormData({
        stringId: '',
        lenderName: '',
        financingType: 'working-capital',
        tier: 1,
        crmAccountId: ''
      });
      
      // Refresh the parent component's data
      if (onLenderCreated) {
        onLenderCreated();
      }
      
    } catch (error) {
      console.error('Error creating API lender:', error);
      setFormError(error.message);
      notifyError(error.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {formError && (
        <Alert variant="danger" className="mb-3">
          {formError}
        </Alert>
      )}
      
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Lender Name *</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g., Alliance Funding"
              value={formData.lenderName}
              onChange={(e) => handleInputChange('lenderName', e.target.value)}
              disabled={creating}
              required
            />
            <Form.Text className="text-muted">
              Enter the full name of the lender. The lender file must match the generated ID (e.g., 'alliance-funding.js').
            </Form.Text>
            {formData.lenderName && formData.stringId && (
              <Form.Text className="text-info">
                <strong>Generated Lender ID:</strong> {formData.stringId} (requires {formData.stringId}.js file)
              </Form.Text>
            )}
          </Form.Group>
        </Col>
      </Row>
      
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Financing Type *</Form.Label>
            <Form.Select
              value={formData.financingType}
              onChange={(e) => handleInputChange('financingType', e.target.value)}
              disabled={creating}
            >
              <option value="working-capital">Working Capital</option>
              <option value="equipment-financing">Equipment Financing</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Tier</Form.Label>
            <Form.Select
              value={formData.tier}
              onChange={(e) => handleInputChange('tier', parseInt(e.target.value))}
              disabled={creating}
            >
              <option value={1}>1</option>
              <option value={2}>2</option>
              <option value={3}>3</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>CRM Account ID</Form.Label>
            <Form.Control
              type="text"
              placeholder="Optional CRM ID"
              value={formData.crmAccountId}
              onChange={(e) => handleInputChange('crmAccountId', e.target.value)}
              disabled={creating}
              maxLength={100}
            />
          </Form.Group>
        </Col>
      </Row>
      
      <div className="d-flex justify-content-end">
        <Button
          type="submit"
          variant="primary"
          disabled={creating}
        >
          {creating ? (
            <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : 'Create API Lender'}
        </Button>
      </div>
    </Form>
  );
};

/**
 * LenderSettings component for configuring lender settings
 * 
 * This component allows administrators to:
 * - Enable/disable lenders (active/inactive)
 * - Enable/disable watermarking for specific lenders
 * - Enable/disable contact info scrambling for specific lenders
 * - View and modify security settings for each lender
 * 
 * @returns {JSX.Element} Lender settings component
 */
const LenderSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { success, error: notifyError } = useNotifications();
  
  // State variables
  const [lenderSettings, setLenderSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingIndividual, setSavingIndividual] = useState(new Set()); // Track which individual lenders are being saved
  const [showAddLenderForm, setShowAddLenderForm] = useState(false);
  const [showAddApiLenderForm, setShowAddApiLenderForm] = useState(false);
  const [expandedLenders, setExpandedLenders] = useState(new Set()); // Track which lenders are expanded
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lenderToDelete, setLenderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // Filter for working capital or equipment financing
  
  // Ref to track if a save is already in progress
  const saveInProgressRef = useRef(false);
  
  // Handle lender card expansion/collapse
  const toggleLenderExpansion = (stringId) => {
    setExpandedLenders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stringId)) {
        newSet.delete(stringId);
      } else {
        newSet.add(stringId);
      }
      return newSet;
    });
  };
  
  // Handle delete lender confirmation
  const handleDeleteLender = (lender) => {
    setLenderToDelete(lender);
    setShowDeleteModal(true);
  };

  // Confirm delete lender
  const confirmDeleteLender = async () => {
    if (!lenderToDelete) return;

    try {
      setDeleting(true);
      setError(null);

      await deleteLender(lenderToDelete.stringId);

      // Remove lender from local state
      setLenderSettings(prev => 
        prev.filter(lender => lender.stringId !== lenderToDelete.stringId)
      );

      success(`${lenderToDelete.lenderName} has been deleted successfully`);
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setLenderToDelete(null);
    } catch (err) {
      console.error('Error deleting lender:', err);
      const errorMessage = err.response?.data?.message || `Failed to delete ${lenderToDelete.lenderName}`;
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  // Cancel delete lender
  const cancelDeleteLender = () => {
    setShowDeleteModal(false);
    setLenderToDelete(null);
  };

  // Save individual lender settings
  const handleSaveIndividualLender = async (stringId) => {
    try {
      setSavingIndividual(prev => new Set(prev).add(stringId));
      setError(null);
      
      // Find the specific lender to save
      const lenderToSave = lenderSettings.find(lender => lender.stringId === stringId);
      
      if (!lenderToSave) {
        throw new Error('Lender not found');
      }
      
      // Prepare single lender settings for saving
      const settingsToSave = [{
        stringId: lenderToSave.stringId,
        lenderName: lenderToSave.lenderName,
        watermarkEnabled: lenderToSave.watermarkEnabled,
        contactInfoScramble: lenderToSave.contactInfoScramble,
        active: lenderToSave.active,
        emailOnly: lenderToSave.emailOnly,
        tier: lenderToSave.tier,
        financingType: lenderToSave.financingType,
        uniqueLenderId: lenderToSave.uniqueLenderId,
        emailToAddress: lenderToSave.emailToAddress,
        emailCcAddress: lenderToSave.emailCcAddress,
        emailSubject: lenderToSave.emailSubject,
        emailBody: lenderToSave.emailBody,
        isCustomEmailLender: lenderToSave.isCustomEmailLender,
        crmAccountId: lenderToSave.crmAccountId
      }];
      
      // Save settings to backend
      const updatedSettings = await saveLenderSettings(settingsToSave);
      
      // Update local state with any new uniqueLenderIds from the backend
      if (updatedSettings && Array.isArray(updatedSettings) && updatedSettings.length > 0) {
        const updatedLender = updatedSettings[0];
        
        setLenderSettings(prev => 
          prev.map(lender => 
            lender.stringId === stringId 
              ? { ...lender, uniqueLenderId: updatedLender.uniqueLenderId }
              : lender
          )
        );
      }
      
      success(`${lenderToSave.lenderName} settings saved successfully`);
    } catch (err) {
      console.error('Error saving individual lender settings:', err);
      const errorMessage = `Failed to save ${lenderSettings.find(l => l.stringId === stringId)?.lenderName || 'lender'} settings`;
      setError(errorMessage);
      notifyError(errorMessage);
    } finally {
      setSavingIndividual(prev => {
        const newSet = new Set(prev);
        newSet.delete(stringId);
        return newSet;
      });
    }
  };

  // Check if user is admin or developer
  useEffect(() => {
    if (user && user.role !== 'super_admin' && user.role !== 'developer') {
      navigate('/');
    }
  }, [user, navigate]);

  // Separate function to fetch settings to avoid React hook dependency issues
  const fetchLenderSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch existing settings from the backend - database is the single source of truth
      const savedSettings = await getLenderSettings();
      
      if (savedSettings && Array.isArray(savedSettings)) {
        // Use database as single source of truth
        const databaseLenders = savedSettings.map(setting => ({
          stringId: setting.stringId,
          lenderName: setting.lenderName || 'Unknown Lender',
          watermarkEnabled: setting.watermarkEnabled || false,
          contactInfoScramble: setting.contactInfoScramble || false,
          active: setting.active !== undefined ? setting.active : true,
          emailOnly: setting.emailOnly || false,
          tier: setting.tier || 1,
          financingType: setting.financingType || 'working-capital',
          uniqueLenderId: setting.uniqueLenderId,
          emailToAddress: setting.emailToAddress || null,
          emailCcAddress: setting.emailCcAddress || null,
          emailSubject: setting.emailSubject || null,
          emailBody: setting.emailBody || null,
          isCustomEmailLender: setting.isCustomEmailLender || false,
          crmAccountId: setting.crmAccountId || null
        }));
        
        console.log(`Loaded ${savedSettings.length} lender settings from database`);
        setLenderSettings(databaseLenders);
      } else {
        // Database is empty - show empty state
        console.log('Database is empty, no lenders to display');
        setLenderSettings([]);
      }
      
    } catch (err) {
      console.error('Error fetching lender settings:', err);
      setError('Failed to load lender settings. Please try again later.');
      notifyError('Failed to load lender settings');
      setLenderSettings([]); // Set empty array instead of falling back to hardcoded lenders
    } finally {
      setLoading(false);
    }
  }, [notifyError]); // Dependencies for useCallback
  
  // Fetch data on component mount
  useEffect(() => {
    fetchLenderSettings();
  }, [fetchLenderSettings]);
  
  // Handle toggle change for watermark setting
  const handleWatermarkToggleChange = (stringId, checked) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, watermarkEnabled: checked } 
          : lender
      )
    );
  };
  
  // Handle toggle change for contact info scramble setting
  const handleScrambleToggleChange = (stringId, checked) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, contactInfoScramble: checked } 
          : lender
      )
    );
  };
  
  // Handle toggle change for active setting
  const handleActiveToggleChange = (stringId, checked) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, active: checked } 
          : lender
      )
    );
  };
  
  // Handle toggle change for emailOnly setting
  const handleEmailOnlyToggleChange = (stringId, checked) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, emailOnly: checked } 
          : lender
      )
    );
  };
  
  // Handle tier change
  const handleTierChange = (stringId, tier) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, tier: parseInt(tier) } 
          : lender
      )
    );
  };
  
  // NEW: Handle email address changes
  const handleEmailToAddressChange = (stringId, emailToAddress) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, emailToAddress } 
          : lender
      )
    );
  };
  
  // NEW: Handle CC email address changes
  const handleEmailCcAddressChange = (stringId, emailCcAddress) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, emailCcAddress } 
          : lender
      )
    );
  };
  
  // NEW: Handle email subject changes
  const handleEmailSubjectChange = (stringId, emailSubject) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, emailSubject } 
          : lender
      )
    );
  };
  
  // NEW: Handle email body changes
  const handleEmailBodyChange = (stringId, emailBody) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, emailBody } 
          : lender
      )
    );
  };
  
  // NEW: Handle CRM Account ID changes
  const handleCrmAccountIdChange = (stringId, crmAccountId) => {
    setLenderSettings(prev => 
      prev.map(lender => 
        lender.stringId === stringId 
          ? { ...lender, crmAccountId } 
          : lender
      )
    );
  };
  
  // Save settings to backend with debounce protection
  const handleSaveSettings = async () => {
    // Prevent multiple rapid saves
    if (saveInProgressRef.current) return;
    
    try {
      saveInProgressRef.current = true;
      setSaving(true);
      setError(null);
      
      for (const lender of lenderSettings) {
        if (lender.emailOnly && !String(lender.emailToAddress || '').trim()) {
          throw new Error(`${lender.lenderName} requires at least one Email To Address.`);
        }

        const invalidToEmails = getInvalidEmails(lender.emailToAddress);
        const invalidCcEmails = getInvalidEmails(lender.emailCcAddress);
        if (invalidToEmails.length > 0) {
          throw new Error(`${lender.lenderName} has invalid Email To Address value(s): ${invalidToEmails.join(', ')}`);
        }
        if (invalidCcEmails.length > 0) {
          throw new Error(`${lender.lenderName} has invalid Email CC Address value(s): ${invalidCcEmails.join(', ')}`);
        }
      }

      // Prepare settings for saving - include email configuration
      const settingsToSave = lenderSettings.map(({ stringId, lenderName, watermarkEnabled, contactInfoScramble, active, emailOnly, tier, financingType, uniqueLenderId, emailToAddress, emailCcAddress, emailSubject, emailBody, isCustomEmailLender, crmAccountId }) => ({
        stringId,
        lenderName,
        watermarkEnabled,
        contactInfoScramble,
        active,
        emailOnly,
        tier,
        financingType,
        uniqueLenderId,
        emailToAddress: normalizeEmailList(emailToAddress),
        emailCcAddress: normalizeEmailList(emailCcAddress),
        emailSubject,
        emailBody,
        isCustomEmailLender,
        crmAccountId
      }));
      
      // Save settings to backend
      const updatedSettings = await saveLenderSettings(settingsToSave);
      
      // Update local state with any new uniqueLenderIds from the backend
      if (updatedSettings && Array.isArray(updatedSettings)) {
        const updatedSettingsMap = updatedSettings.reduce((acc, setting) => {
          acc[setting.stringId] = setting;
          return acc;
        }, {});
        
        setLenderSettings(prev => 
          prev.map(lender => {
            const updated = updatedSettingsMap[lender.stringId];
            
            if (updated) {
              return {
                ...lender,
                uniqueLenderId: updated.uniqueLenderId
              };
            }
            
            return lender;
          })
        );
      }
      
      success('Lender settings saved successfully');
    } catch (err) {
      console.error('Error saving lender settings:', err);
      setError('Failed to save lender settings. Please try again later.');
      notifyError('Failed to save lender settings');
    } finally {
      setSaving(false);
      // Add a small delay before allowing another save
      setTimeout(() => {
        saveInProgressRef.current = false;
      }, 1000);
    }
  };
  

  
  // Filter lenders based on selected financing type
  const filteredLenders = lenderSettings.filter(lender => 
    activeFilter === 'all' || lender.financingType === activeFilter
  );
  
  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading lender settings...</p>
      </Container>
    );
  }
  
  return (
    <Container className="my-5">
      <Card className="mb-4 shadow-sm">
        <Card.Body>
          <Row className="align-items-center mb-4">
            <Col>
              <h2 className="mb-0">Lender Settings</h2>
              <p className="text-muted mt-1">
                Add new lender integrations & configure lender availability, lender tiers, email submission settings, security features for each lender
              </p>
            </Col>
          </Row>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error}
            </Alert>
          )}
          
          <Alert variant="info" className="mb-4">
            
            <p>
              <strong>Active:</strong> Controls whether the lender is available for submissions. 
              Inactive lenders will not appear in the lender submission forms.
            </p>
            <p>
              <strong>Email Only:</strong> Indicates whether this lender only accepts submissions via email 
              rather than through API integration. Email-only lenders require manual processing.
            </p>
            <p>
              <strong>Tier:</strong> Priority level for lender display (1 = highest priority, 3 = lowest priority). 
              Lower tier numbers appear first in the submission interface.
            </p>
            <p>
              <strong>Watermark:</strong> Adds your company logo diagonally across each page of bank statements 
              that are submitted to lenders. This helps with branding and document tracking.
            </p>
            <p className="mb-0">
              <strong>Contact Info Scramble:</strong> Protects sensitive information by modifying contact details
              before submitting to lenders. Enable or disable these features for each lender individually using the controls below.
            </p>
          </Alert>
          
          {/* Financing Type Filter */}
          <div className="mb-3 d-flex align-items-center">
            <span className="me-3 fw-bold">Filter Lenders:</span>
            <Form.Group>
              <div className="d-flex">
                <Form.Check
                  type="radio"
                  id="filter-all"
                  name="financing-type-filter"
                  label="All"
                  checked={activeFilter === 'all'}
                  onChange={() => setActiveFilter('all')}
                  className="me-3"
                />
                <Form.Check
                  type="radio"
                  id="filter-working-capital"
                  name="financing-type-filter"
                  label="Working Capital"
                  checked={activeFilter === 'working-capital'}
                  onChange={() => setActiveFilter('working-capital')}
                  className="me-3"
                />
                <Form.Check
                  type="radio"
                  id="filter-equipment-financing"
                  name="financing-type-filter"
                  label="Equipment Financing"
                  checked={activeFilter === 'equipment-financing'}
                  onChange={() => setActiveFilter('equipment-financing')}
                />
              </div>
            </Form.Group>
          </div>
          
          {/* Lender Cards */}
          <div className="mb-4">
            {filteredLenders.map((lender) => {
              const isExpanded = expandedLenders.has(lender.stringId);
              return (
                <Card key={lender.stringId} className="mb-3 border shadow-sm">
                  <Card.Header 
                    className="d-flex justify-content-between align-items-center bg-light cursor-pointer"
                    onClick={() => toggleLenderExpansion(lender.stringId)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0 me-3">{lender.lenderName}</h5>
                      <Badge bg={lender.active ? 'light' : 'secondary'} text={lender.active ? 'success' : 'light'} className="me-2">
                        {lender.active ? 'Active' : 'Inactive'}
                      </Badge>
                      <Badge bg="light" text={lender.emailOnly ? 'warning' : 'info'} className="me-2">
                        {lender.emailOnly ? 'Email Only' : 'API'}
                      </Badge>
                      <Badge bg="light" text="primary">
                        Tier {lender.tier}
                      </Badge>
                    </div>
                    <div className="d-flex align-items-center">
                      <small className="text-muted me-2">
                        {lender.financingType === 'working-capital' ? 'Working Capital' : 'Equipment Financing'}
                      </small>
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                    </div>
                  </Card.Header>
                  
                  {isExpanded && (
                    <Card.Body>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <h6>Basic Settings</h6>
                            <hr className="my-2" />
                            
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <Form.Label className="mb-0">Active</Form.Label>
                              <Form.Check
                                type="switch"
                                id={`active-switch-${lender.stringId}`}
                                checked={lender.active}
                                onChange={(e) => handleActiveToggleChange(lender.stringId, e.target.checked)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                              />
                            </div>
                            
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <Form.Label className="mb-0">Email Only</Form.Label>
                              <Form.Check
                                type="switch"
                                id={`emailOnly-switch-${lender.stringId}`}
                                checked={lender.emailOnly}
                                onChange={(e) => handleEmailOnlyToggleChange(lender.stringId, e.target.checked)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                              />
                            </div>
                            
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <Form.Label className="mb-0">Tier</Form.Label>
                              <Form.Select
                                value={lender.tier}
                                onChange={(e) => handleTierChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                                size="sm"
                                style={{ width: '80px' }}
                              >
                                {[1, 2, 3].map((tier) => (
                                  <option key={tier} value={tier}>
                                    {tier}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                            
                            <Form.Group className="mb-3">
                              <Form.Label>CRM Account ID</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Enter CRM Account ID"
                                value={lender.crmAccountId || ''}
                                onChange={(e) => handleCrmAccountIdChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                                maxLength={100}
                              />
                              <Form.Text className="text-muted">
                                Optional ID for tracking this lender in your CRM system
                              </Form.Text>
                            </Form.Group>
                            
                            <h6>Security Settings</h6>
                            <hr className="my-2" />
                            
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <Form.Label className="mb-0">Watermark</Form.Label>
                              <Form.Check
                                type="switch"
                                id={`watermark-switch-${lender.stringId}`}
                                checked={lender.watermarkEnabled}
                                onChange={(e) => handleWatermarkToggleChange(lender.stringId, e.target.checked)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                              />
                            </div>
                            
                            <div className="d-flex align-items-center justify-content-between">
                              <Form.Label className="mb-0">Contact Scramble</Form.Label>
                              <Form.Check
                                type="switch"
                                id={`scramble-switch-${lender.stringId}`}
                                checked={lender.contactInfoScramble}
                                onChange={(e) => handleScrambleToggleChange(lender.stringId, e.target.checked)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                              />
                            </div>
                          </div>
                        </Col>
                        
                        <Col md={6}>
                          <div className="mb-3">
                            <h6>Email Settings</h6>
                            <hr className="my-2" />
                            
                            <Form.Group className="mb-3">
                              <Form.Label>Email To Address</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder={lender.emailOnly ? "Required; separate multiple emails with commas or semicolons" : "Optional; separate multiple emails with commas or semicolons"}
                                value={lender.emailToAddress || ''}
                                onChange={(e) => handleEmailToAddressChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                                isInvalid={lender.emailOnly && !lender.emailToAddress}
                              />
                              <Form.Text className="text-muted">
                                Separate multiple addresses with commas or semicolons.
                              </Form.Text>
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                              <Form.Label>Email CC Address</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Optional; separate multiple emails with commas or semicolons"
                                value={lender.emailCcAddress || ''}
                                onChange={(e) => handleEmailCcAddressChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                              />
                              <Form.Text className="text-muted">
                                Separate multiple addresses with commas or semicolons.
                              </Form.Text>
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                              <Form.Label>Email Subject</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Default: Business Name"
                                value={lender.emailSubject || ''}
                                onChange={(e) => handleEmailSubjectChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                                maxLength={200}
                              />
                            </Form.Group>
                            
                            <Form.Group className="mb-0">
                              <Form.Label>Email Body</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Default: Simple message"
                                value={lender.emailBody || ''}
                                onChange={(e) => handleEmailBodyChange(lender.stringId, e.target.value)}
                                disabled={saving || savingIndividual.has(lender.stringId)}
                                maxLength={2000}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteLender(lender)}
                          disabled={saving || savingIndividual.has(lender.stringId) || deleting}
                        >
                          <i className="fas fa-trash me-1"></i>
                          Delete Lender
                        </Button>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleSaveIndividualLender(lender.stringId)}
                          disabled={saving || savingIndividual.has(lender.stringId) || deleting}
                        >
                          {savingIndividual.has(lender.stringId) ? (
                            <>
                              <Spinner as="span" animation="border" size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : 'Save'}
                        </Button>
                      </div>
                    </Card.Body>
                  )}
                </Card>
              );
            })}
          </div>
          
          {/* Save Settings Button - moved to be right after lenders */}
          <div className="d-flex justify-content-end mb-4">
            <Button
              variant="primary"
              onClick={handleSaveSettings}
              disabled={saving || saveInProgressRef.current || savingIndividual.size > 0}
            >
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Saving All...
                </>
              ) : 'Save All Settings'}
            </Button>
          </div>
          
          {/* Custom Email Lender Creation Section */}
          <hr className="my-4" />
          
          <Row className="mb-3">
            <Col>
              <h4>Add Custom Email-Only Lender</h4>
              <p className="text-muted">
                Create a new email-only lender that will be stored in the database. This allows you to add 
                lenders that aren't in the predefined lists but need email submission capabilities.
              </p>
            </Col>
          </Row>
          
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              {!showAddLenderForm ? (
                <div className="text-center">
                  <Button
                    variant="success"
                    onClick={() => setShowAddLenderForm(true)}
                  >
                    Add Email Lender
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0">Create New Email-Only Lender</h5>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setShowAddLenderForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                  <CustomEmailLenderForm onLenderCreated={(result) => {
                    fetchLenderSettings();
                    setShowAddLenderForm(false);
                  }} />
                </div>
              )}
            </Card.Body>
          </Card>
          
          {/* API Lender Creation Section (developers only) */}
          {user?.role === 'developer' && (
            <>
              <hr className="my-4" />
              
              <Row className="mb-3">
                <Col>
                  <h4>Add API Lender <Badge bg="info">Developer Only</Badge></h4>
                  <p className="text-muted">
                    Create a new API lender that integrates with an existing lender file. This functionality is only 
                    available to developers and requires that the corresponding lender file (e.g., alliance.js) already 
                    exists in the lenders directory with a proper submit function.
                  </p>
                </Col>
              </Row>
              
              <Card className="mb-4 border-0 shadow-sm">
                <Card.Body>
                  {!showAddApiLenderForm ? (
                    <div className="text-center">
                      <Button
                        variant="primary"
                        onClick={() => setShowAddApiLenderForm(true)}
                      >
                        Add API Lender
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">Create New API Lender</h5>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setShowAddApiLenderForm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                      <ApiLenderForm onLenderCreated={(result) => {
                        fetchLenderSettings();
                        setShowAddApiLenderForm(false);
                      }} />
                    </div>
                  )}
                </Card.Body>
              </Card>
            </>
          )}
          
          {/* Lender ID Reference Section */}
          <hr className="my-4" />
          
          <Row className="mb-3">
            <Col>
              <h4>Lender ID Reference</h4>
              <p className="text-muted">
                Use this reference table to identify lenders from watermarked documents. When a deal comes back 
                in a backdoor situation, you can match the numbered watermark to the unique lender ID below.
              </p>
            </Col>
          </Row>
          
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '60%' }}>Lender Name</th>
                    <th style={{ width: '40%' }}>Unique Lender ID</th>
                  </tr>
                </thead>
                <tbody>
                  {lenderSettings
                    .filter(lender => lender.uniqueLenderId !== null)
                    .sort((a, b) => a.lenderName.localeCompare(b.lenderName))
                    .map((lender) => (
                      <tr key={lender.stringId}>
                        <td>{lender.lenderName}</td>
                        <td>
                          <Badge bg="secondary" className="fs-6">
                            {lender.uniqueLenderId}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  {lenderSettings.filter(lender => lender.uniqueLenderId !== null).length === 0 && (
                    <tr>
                      <td colSpan="2" className="text-center text-muted py-4">
                        No lender IDs assigned yet. Save your lender settings above to generate unique IDs.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={cancelDeleteLender} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Lender</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lenderToDelete && (
            <>
              <div className="text-center mb-3">
                <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
              </div>
              <p className="text-center">
                Are you sure you want to delete <strong>{lenderToDelete.lenderName}</strong>?
              </p>
              <div className="alert alert-warning">
                <strong>Warning:</strong> This action cannot be undone. The lender will be permanently removed from the database.
              </div>
              {lenderToDelete.isCustomEmailLender && (
                <div className="alert alert-info">
                  <strong>Note:</strong> This is a custom email-only lender that was created through this interface.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDeleteLender} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteLender} disabled={deleting}>
            {deleting ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              <>
                <i className="fas fa-trash me-1"></i>
                Delete Lender
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default LenderSettings;
